import React from 'react';
import {Trans} from 'react-i18next';
import {useTranslation} from '@wix/yoshi-flow-editor';
import {Text} from 'wix-ui-tpa/cssVars';
import {
  PolicyButtonWithDialog,
  PolicyButtonLocation,
  PolicyType,
} from '../../../../common/components/PolicyButtonWithDialog/PolicyButtonWithDialog';
import {classes} from '../Checkboxes.st.css';
import {useControllerProps} from '../../Widget/ControllerContext';

export interface PolicyInfo {
  labelKey: string;
  dialogTitle: string;
  dataHook: string;
  enabled?: boolean | null;
  value: () => React.ReactNode;
  policyType: PolicyType;
  location: PolicyButtonLocation;
}

export const PoliciesText = ({
  dataHook,
  policies,
  labelKey,
}: {
  dataHook: string;
  policies: PolicyInfo[];
  labelKey?: string;
}) => {
  const listOfPolicies = policies.reduce<React.ReactElement | undefined>((acc, policy, index) => {
    if (acc === undefined) {
      return <PolicyText policy={policy} />;
    }

    if (index === policies.length - 1) {
      return <AddLastItemToList list={acc} item={<PolicyText policy={policy} />} />;
    }

    return <AddItemToList item={<PolicyText policy={policy} />} list={acc} />;
  }, undefined);

  return (
    <Text data-hook={dataHook}>
      <PolicyLabel listOfPolicies={listOfPolicies!} labelKey={labelKey} />
    </Text>
  );
};

export const PolicyLabel = ({listOfPolicies, labelKey}: {listOfPolicies: React.ReactElement; labelKey?: string}) => {
  const {t, i18n} = useTranslation();
  return (
    <Trans
      i18n={i18n}
      t={t}
      i18nKey={labelKey ?? 'checkout.terms_and_conditions.checkbox.label'}
      components={[listOfPolicies]}
    />
  );
};

export const AddItemToList = ({item, list}: {item: React.ReactElement; list: React.ReactElement}) => {
  const {t, i18n} = useTranslation();
  return (
    <Trans
      i18n={i18n}
      t={t}
      i18nKey="checkout.terms_and_conditions.checkbox.add_item_to_list"
      components={[list, item]}
    />
  );
};

export const AddLastItemToList = ({item, list}: {item: React.ReactElement; list: React.ReactElement}) => {
  const {t, i18n} = useTranslation();
  return (
    <Trans i18n={i18n} t={t} i18nKey="checkout.terms_and_conditions.checkbox.add_last_item" components={[list, item]} />
  );
};

export const PolicyText = ({policy}: {policy: PolicyInfo}) => {
  const {t, i18n} = useTranslation();
  const {
    checkoutStore: {onPolicyClicked},
  } = useControllerProps();
  return (
    <Trans
      i18n={i18n}
      t={t}
      i18nKey={policy.labelKey}
      components={[
        <PolicyButtonWithDialog
          className={classes.policy}
          key={policy.labelKey}
          show={true}
          dialogTitle={policy.dialogTitle}
          dataHook={policy.dataHook}
          location={policy.location}
          policyType={policy.policyType}
          onPolicyClicked={onPolicyClicked}
          dialogDescription={policy.value}>
          content
        </PolicyButtonWithDialog>,
      ]}
    />
  );
};
